import React from 'react';
import { ChevronDown, ChevronUp, Mail, Plus, RefreshCw, Search } from 'lucide-react';
import { useParams, useSearchParams } from 'react-router-dom';
import {
  addTicketNote,
  createTicket,
  fetchQuotes,
  fetchTickets,
  searchTickets,
  sendTransactionalEmail,
  updateTicketStatus,
} from '../lib/api';
import { buildSkyluxTicketDeskClientMailHtml } from '../lib/skyluxClientMailLayouts';
import { buildSkyluxQuoteClientMailHtml, buildSkyluxQuoteEmailIntroPlain, buildPaymentEmailSubject } from '../lib/skyluxQuoteMailHtml';
import {
  isQuoteFollowUpTicket,
  parseQuoteIdFromTicket,
} from '../lib/quoteTicketLink';
import { sendTicketReceiptEmail } from '../lib/sendTicketReceiptEmail';
import { mergeQuotes } from '../lib/mergeQuotes';
import { normalizePassengers } from '../lib/passengers';
import { appendEmailLog, loadCms, loadQuotes, saveQuote } from '../lib/skyluxStore';
import { routeSummaryLabel } from '../lib/roundTrip';
import { formatEmailLogSuccessFooter } from '../lib/emailLogDelivery';
import type { QuoteRecord, Ticket, TicketPriority, TicketStatus } from '../types';
import MissingAgentNotice from '../components/MissingAgentNotice';
import TicketQuotePanel, { priorityBadgeClass, statusBadgeClass } from '../components/admin/TicketQuotePanel';
import { useAgent } from '../context/AgentsContext';
import '../styles/admin-tickets.css';

const statusOrder: TicketStatus[] = ['open', 'in_progress', 'waiting_customer', 'resolved', 'closed'];

const FOCUS_TICKET_STORAGE_KEY = 'skylux_focus_ticket_id';

function formatStatusLabel(status: TicketStatus): string {
  return status.replace(/_/g, ' ');
}

function paymentHint(ticket: Ticket, quote: QuoteRecord | undefined): string | null {
  if (!isQuoteFollowUpTicket(ticket) || !quote) return null;
  if (quote.paymentStatus === 'paid') return 'Paid';
  const n = normalizePassengers(quote).filter((p) => p.name.trim()).length;
  if (n > 0) return `${n} pax · unpaid`;
  return 'Awaiting booking';
}

const AdminTickets = () => {
  const { agentId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const agent = useAgent(agentId);
  const [tickets, setTickets] = React.useState<Ticket[]>([]);
  const [quotesById, setQuotesById] = React.useState<Map<string, QuoteRecord>>(new Map());
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [query, setQuery] = React.useState('');
  const [selectedId, setSelectedId] = React.useState<string | null>(null);
  const [noteText, setNoteText] = React.useState('');
  const [emailingId, setEmailingId] = React.useState<string | null>(null);
  const [sendingTicketId, setSendingTicketId] = React.useState<string | null>(null);
  const [sendingOafId, setSendingOafId] = React.useState<string | null>(null);
  const [resendAllInProgress, setResendAllInProgress] = React.useState(false);
  const [createOpen, setCreateOpen] = React.useState(false);
  const [form, setForm] = React.useState({
    title: '',
    description: '',
    clientName: '',
    clientEmail: '',
    priority: 'medium' as TicketPriority,
    tags: '',
  });

  const loadQuotesMap = React.useCallback(async () => {
    try {
      const fromApi = await fetchQuotes();
      const merged = mergeQuotes(fromApi, loadQuotes());
      setQuotesById(new Map(merged.map((q) => [q.id, q])));
    } catch {
      setQuotesById(new Map(loadQuotes().map((q) => [q.id, q])));
    }
  }, []);

  const load = React.useCallback(
    async (searchValue = '') => {
      try {
        setLoading(true);
        const rows = searchValue.trim() ? await searchTickets(searchValue) : await fetchTickets();
        setTickets(rows);
        setError(null);
        await loadQuotesMap();
        setSelectedId((prev) => {
          if (prev && rows.some((t) => t.id === prev)) return prev;
          return rows[0]?.id ?? null;
        });
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Failed to load tickets');
      } finally {
        setLoading(false);
      }
    },
    [loadQuotesMap],
  );

  const scrollTicketIntoViewRef = React.useRef<string | null>(null);
  const skipInitialLoadAfterFocusRef = React.useRef(false);

  React.useLayoutEffect(() => {
    const fromUrl = searchParams.get('ticket');
    let fromStorage: string | null = null;
    try {
      fromStorage = sessionStorage.getItem(FOCUS_TICKET_STORAGE_KEY);
    } catch {
      fromStorage = null;
    }
    const id = fromUrl || fromStorage;
    if (!id) return;
    try {
      sessionStorage.removeItem(FOCUS_TICKET_STORAGE_KEY);
    } catch {
      // no-op
    }
    scrollTicketIntoViewRef.current = id;
    setQuery('');
    setSelectedId(id);
    skipInitialLoadAfterFocusRef.current = true;
    void load('');
    if (fromUrl) {
      setSearchParams(
        (prev) => {
          const n = new URLSearchParams(prev);
          n.delete('ticket');
          return n;
        },
        { replace: true },
      );
    }
  }, [searchParams, setSearchParams, load]);

  React.useEffect(() => {
    const id = scrollTicketIntoViewRef.current;
    if (!id || loading) return;
    if (!tickets.some((t) => t.id === id)) return;
    requestAnimationFrame(() => {
      document.getElementById(`ticket-row-${id}`)?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    });
    scrollTicketIntoViewRef.current = null;
  }, [loading, tickets]);

  React.useEffect(() => {
    if (skipInitialLoadAfterFocusRef.current) {
      skipInitialLoadAfterFocusRef.current = false;
      return;
    }
    void load();
  }, [load]);

  React.useEffect(() => {
    const fn = () => void loadQuotesMap();
    window.addEventListener('skylux-storage', fn);
    return () => window.removeEventListener('skylux-storage', fn);
  }, [loadQuotesMap]);

  const selectedTicket = React.useMemo(
    () => tickets.find((t) => t.id === selectedId) ?? null,
    [tickets, selectedId],
  );

  const selectedQuote = React.useMemo(() => {
    if (!selectedTicket) return undefined;
    const qid = parseQuoteIdFromTicket(selectedTicket);
    return qid ? quotesById.get(qid) : undefined;
  }, [selectedTicket, quotesById]);

  function copyText(text: string, label: string) {
    void navigator.clipboard.writeText(text).then(
      () => window.alert(`${label} copied.`),
      () => window.alert('Could not copy to clipboard.'),
    );
  }

  if (!agent) {
    return (
      <div className="animate-fade">
        <MissingAgentNotice />
      </div>
    );
  }

  async function onCreate(e: React.FormEvent) {
    e.preventDefault();
    try {
      const ticket = await createTicket({
        title: form.title,
        description: form.description,
        clientName: form.clientName,
        clientEmail: form.clientEmail,
        priority: form.priority,
        assignee: agentId,
        tags: form.tags.split(',').map((x) => x.trim()).filter(Boolean),
      });
      setTickets((prev) => [ticket, ...prev]);
      setSelectedId(ticket.id);
      setForm({ title: '', description: '', clientName: '', clientEmail: '', priority: 'medium', tags: '' });
      setCreateOpen(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to create ticket');
    }
  }

  async function onCycleStatus(ticket: Ticket) {
    const next = statusOrder[(statusOrder.indexOf(ticket.status) + 1) % statusOrder.length];
    try {
      const updated = await updateTicketStatus(ticket.id, next);
      setTickets((prev) => prev.map((x) => (x.id === updated.id ? updated : x)));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to update ticket');
    }
  }

  async function onEmailClient(ticket: Ticket) {
    if (!agent) return;
    const to = ticket.clientEmail.trim();
    if (!to) {
      setError('This ticket has no client email.');
      return;
    }
    const subject = `SkyLux Travel — Ticket ${ticket.code}`;
    const textBody = [
      `Ticket ${ticket.code}`,
      ticket.title,
      '',
      ticket.description,
      '',
      `Status: ${ticket.status}`,
      `Priority: ${ticket.priority}`,
    ].join('\n');
    const html = buildSkyluxTicketDeskClientMailHtml({
      code: ticket.code,
      title: ticket.title,
      description: ticket.description,
      clientName: ticket.clientName,
      status: ticket.status,
      priority: ticket.priority,
    });
    setEmailingId(ticket.id);
    setError(null);
    let mailFooter = '';
    let fromAddr = agent.email;
    try {
      const mail = await sendTransactionalEmail({ to, subject, text: textBody, html });
      fromAddr = mail.from;
      mailFooter = `\n\n— Sent via SMTP from ${mail.from}${mail.messageId ? ` (id: ${mail.messageId})` : ''}`;
    } catch (e) {
      mailFooter = `\n\n— Email not delivered: ${e instanceof Error ? e.message : 'Unknown error'}`;
    }
    appendEmailLog({
      id: `em_${Date.now()}`,
      at: new Date().toISOString(),
      agentId: agent.id,
      agentEmail: fromAddr,
      to,
      subject,
      kind: 'ticket_update',
      preview: textBody + mailFooter,
      deliveryStatus: mailFooter.includes('Sent via SMTP') ? 'sent' : 'failed',
      errorMessage: mailFooter.includes('not delivered')
        ? mailFooter.replace(/^[\s\S]*not delivered:\s*/i, '').trim()
        : undefined,
    });
    setEmailingId(null);
    window.alert(mailFooter.includes('Sent via SMTP') ? `Email sent to ${to}.` : mailFooter.replace(/^\n\n— /, ''));
  }

  async function onSendTicketReceipt(ticket: Ticket) {
    if (!agent) return;
    const quoteId = parseQuoteIdFromTicket(ticket);
    if (!quoteId) {
      window.alert('This ticket is not linked to a quote (missing quote id in the description).');
      return;
    }
    const quote = quotesById.get(quoteId);
    if (!quote) {
      setError('Quote data not found. Click Refresh or check the server.');
      return;
    }
    setSendingTicketId(ticket.id);
    setError(null);
    try {
      const result = await sendTicketReceiptEmail(quote, ticket, agent);
      if (result.ok) {
        setQuotesById((prev) => {
          const next = new Map(prev);
          next.set(result.quote.id, result.quote);
          return next;
        });
        window.alert(result.message);
      } else {
        setError(result.message);
        window.alert(result.message);
      }
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Could not send e-ticket email';
      setError(msg);
      window.alert(msg);
    } finally {
      setSendingTicketId(null);
    }
  }

  async function onConfirmPayment(ticket: Ticket) {
    if (!agent) return;
    const quoteId = parseQuoteIdFromTicket(ticket);
    if (!quoteId) return;
    const quote = quotesById.get(quoteId);
    if (!quote) {
      setError('Quote data not found. Click Refresh or check the server.');
      return;
    }
    if (quote.paymentStatus === 'paid') {
      window.alert('Payment is already marked as paid.');
      return;
    }
    const updated: QuoteRecord = { ...quote, paymentStatus: 'paid' };
    saveQuote(updated);
    setQuotesById((prev) => {
      const next = new Map(prev);
      next.set(updated.id, updated);
      return next;
    });
    window.alert('Payment marked as paid.');
  }

  async function onConfirmAndSendTicketReceipt(ticket: Ticket) {
    if (!agent) return;
    const quoteId = parseQuoteIdFromTicket(ticket);
    if (!quoteId) {
      window.alert('This ticket is not linked to a quote (missing quote id in the description).');
      return;
    }
    const quote = quotesById.get(quoteId);
    if (!quote) {
      setError('Quote data not found. Click Refresh or check the server.');
      window.alert('Quote data not found. Click Refresh or check the server.');
      return;
    }
    const paidQuote: QuoteRecord = quote.paymentStatus === 'paid' ? quote : { ...quote, paymentStatus: 'paid' };
    saveQuote(paidQuote);
    setQuotesById((prev) => {
      const next = new Map(prev);
      next.set(paidQuote.id, paidQuote);
      return next;
    });

    setSendingTicketId(ticket.id);
    setError(null);
    try {
      const result = await sendTicketReceiptEmail(paidQuote, ticket, agent, { resend: true });
      if (result.ok) {
        setQuotesById((prev) => {
          const next = new Map(prev);
          next.set(result.quote.id, result.quote);
          return next;
        });
        window.alert(result.message);
      } else {
        setError(result.message);
        window.alert(result.message);
      }
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Could not send e-ticket email';
      setError(msg);
      window.alert(msg);
    } finally {
      setSendingTicketId(null);
    }
  }

  async function onSendOafEmail(ticket: Ticket) {
    if (!agent) return;
    const quoteId = parseQuoteIdFromTicket(ticket);
    if (!quoteId) {
      window.alert('This ticket is not linked to a quote (missing quote id in the description).');
      return;
    }
    const quote = quotesById.get(quoteId);
    if (!quote) {
      setError('Quote data not found. Click Refresh or check the server.');
      return;
    }
    const to = quote.clientSnapshot.email?.trim();
    if (!to) {
      window.alert('Client email missing on this quote.');
      return;
    }

    const cms = loadCms();
    const travelers = normalizePassengers(quote);
    const passengerCount = Math.max(1, travelers.length);
    const totalUsd = quote.pricePerPerson * passengerCount;
    const quoteMailAgent = {
      name: agent.name,
      nickname: agent.nickname,
      email: agent.email,
      avatar: agent.avatar,
    };
    const subject = buildPaymentEmailSubject(quote.flight.arrival, quote.pricePerPerson);
    const preview = [
      buildSkyluxQuoteEmailIntroPlain({
        clientName: quote.clientSnapshot.name,
        agent: quoteMailAgent,
        flight: quote.flight,
        primaryCta: 'payment',
      }),
      '',
      ...travelers.map((row, i) => `${i + 1}. ${row.name}${row.age != null ? ` (age ${row.age})` : ''}`),
      '',
      `Total: ${totalUsd.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} USD`,
      `Complete payment (OAF checkout): ${quote.paymentUrl}`,
    ].join('\n');
    const html = buildSkyluxQuoteClientMailHtml({
      clientName: quote.clientSnapshot.name,
      introText: cms.quoteEmailIntro,
      flight: quote.flight,
      pricePerPerson: quote.pricePerPerson,
      passengerCount,
      bookingUrl: quote.bookingUrl,
      paymentUrl: quote.paymentUrl,
      quoteId: quote.id,
      agent: quoteMailAgent,
      passengers: travelers,
      primaryCta: 'payment',
    });

    setSendingOafId(ticket.id);
    setError(null);
    try {
      const mail = await sendTransactionalEmail({ to, subject, text: preview, html });
      const sentAt = new Date().toISOString();
      const updatedQuote: QuoteRecord = {
        ...quote,
        emailsSent: { ...quote.emailsSent, oaf: sentAt },
      };
      saveQuote(updatedQuote);
      setQuotesById((prev) => {
        const next = new Map(prev);
        next.set(updatedQuote.id, updatedQuote);
        return next;
      });
      appendEmailLog({
        id: `em_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`,
        at: sentAt,
        agentId: agent.id,
        agentEmail: mail.from,
        to,
        subject,
        kind: 'oaf',
        quoteId: quote.id,
        preview: `${preview}\n\n— OAF email: Complete payment${formatEmailLogSuccessFooter(mail.from, mail.messageId)}`,
        deliveryStatus: 'sent',
      });
      window.alert(`OAF payment email sent to ${to}.`);
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Could not send OAF email';
      setError(msg);
      window.alert(msg);
    } finally {
      setSendingOafId(null);
    }
  }

  async function onResendAllEmails(ticket: Ticket) {
    if (!agent) return;
    const quoteId = parseQuoteIdFromTicket(ticket);
    if (!quoteId) return;
    const quote = quotesById.get(quoteId);
    if (!quote) {
      setError('Quote data not found. Click Refresh or check the server.');
      return;
    }
    if (quote.paymentStatus !== 'paid') {
      window.alert('Confirm payment first (mark as paid), then resend all emails.');
      return;
    }
    const to = quote.clientSnapshot.email?.trim();
    if (!to) {
      window.alert('Client email missing on this quote.');
      return;
    }

    setResendAllInProgress(true);
    setError(null);
    try {
      const cms = loadCms();
      const quoteMailAgent = {
        name: agent.name,
        nickname: agent.nickname,
        email: agent.email,
        avatar: agent.avatar,
      };

      // 1) Quote email (ticket quote / booking link)
      const travelersForCount = normalizePassengers(quote);
      const passengerCount = Math.max(1, quote.adults ?? travelersForCount.length);
      const totalUsd = quote.pricePerPerson * passengerCount;
      const quoteDestination = quote.flight.arrival?.trim() || 'Destination';
      const quoteSubject = `SkyLux Travel: Quote - Flight to ${quoteDestination} - $${quote.pricePerPerson.toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`;
      const quotePreview = [
        buildSkyluxQuoteEmailIntroPlain({
          clientName: quote.clientSnapshot.name,
          agent: quoteMailAgent,
          flight: quote.flight,
          primaryCta: 'booking',
        }),
        '',
        `Total: ${totalUsd.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} USD`,
        `Review & book (enter passenger details): ${quote.bookingUrl}`,
      ].join('\n');
      const quoteHtml = buildSkyluxQuoteClientMailHtml({
        clientName: quote.clientSnapshot.name,
        introText: cms.quoteEmailIntro,
        flight: quote.flight,
        pricePerPerson: quote.pricePerPerson,
        passengerCount,
        bookingUrl: quote.bookingUrl,
        paymentUrl: quote.paymentUrl,
        quoteId: quote.id,
        agent: quoteMailAgent,
        primaryCta: 'booking',
      });

      const quoteMail = await sendTransactionalEmail({ to, subject: quoteSubject, text: quotePreview, html: quoteHtml });
      const quoteSentAt = new Date().toISOString();
      const updatedQuoteAfterQuote: QuoteRecord = {
        ...quote,
        emailsSent: { ...quote.emailsSent, quote: quoteSentAt },
      };
      saveQuote(updatedQuoteAfterQuote);
      setQuotesById((prev) => {
        const next = new Map(prev);
        next.set(updatedQuoteAfterQuote.id, updatedQuoteAfterQuote);
        return next;
      });
      appendEmailLog({
        id: `em_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`,
        at: quoteSentAt,
        agentId: agent.id,
        agentEmail: quoteMail.from,
        to,
        subject: quoteSubject,
        kind: 'quote',
        quoteId: quote.id,
        preview: `${quotePreview}\n\n— Quote email (resend): Review & book${formatEmailLogSuccessFooter(quoteMail.from, quoteMail.messageId)}`,
        deliveryStatus: 'sent',
      });

      // 2) OAF email (payment link / passengers on file)
      const travelers = normalizePassengers(updatedQuoteAfterQuote);
      const oafPassengerCount = travelers.length;
      const oafTotalUsd = updatedQuoteAfterQuote.pricePerPerson * oafPassengerCount;
      const oafSubject = buildPaymentEmailSubject(
        updatedQuoteAfterQuote.flight.arrival,
        updatedQuoteAfterQuote.pricePerPerson,
      );
      const oafPreview = [
        buildSkyluxQuoteEmailIntroPlain({
          clientName: updatedQuoteAfterQuote.clientSnapshot.name,
          agent: quoteMailAgent,
          flight: updatedQuoteAfterQuote.flight,
          primaryCta: 'payment',
        }),
        '',
        ...travelers.map((row, i) => `${i + 1}. ${row.name}${row.age != null ? ` (age ${row.age})` : ''}`),
        '',
        `Total: ${oafTotalUsd.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} USD`,
        `Complete payment (OAF checkout): ${updatedQuoteAfterQuote.paymentUrl}`,
      ].join('\n');
      const oafHtml = buildSkyluxQuoteClientMailHtml({
        clientName: updatedQuoteAfterQuote.clientSnapshot.name,
        introText: cms.quoteEmailIntro,
        flight: updatedQuoteAfterQuote.flight,
        pricePerPerson: updatedQuoteAfterQuote.pricePerPerson,
        passengerCount: oafPassengerCount,
        bookingUrl: updatedQuoteAfterQuote.bookingUrl,
        paymentUrl: updatedQuoteAfterQuote.paymentUrl,
        quoteId: updatedQuoteAfterQuote.id,
        agent: quoteMailAgent,
        passengers: travelers,
        primaryCta: 'payment',
      });

      const oafMail = await sendTransactionalEmail({ to, subject: oafSubject, text: oafPreview, html: oafHtml });
      const oafSentAt = new Date().toISOString();
      const updatedQuoteAfterOaf: QuoteRecord = {
        ...updatedQuoteAfterQuote,
        emailsSent: { ...updatedQuoteAfterQuote.emailsSent, oaf: oafSentAt },
      };
      saveQuote(updatedQuoteAfterOaf);
      setQuotesById((prev) => {
        const next = new Map(prev);
        next.set(updatedQuoteAfterOaf.id, updatedQuoteAfterOaf);
        return next;
      });
      appendEmailLog({
        id: `em_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`,
        at: oafSentAt,
        agentId: agent.id,
        agentEmail: oafMail.from,
        to,
        subject: oafSubject,
        kind: 'oaf',
        quoteId: quote.id,
        preview: `${oafPreview}\n\n— OAF email (resend): Complete payment${formatEmailLogSuccessFooter(oafMail.from, oafMail.messageId)}`,
        deliveryStatus: 'sent',
      });

      // 3) E-ticket receipt (can resend even if already delivered)
      const ticketResult = await sendTicketReceiptEmail(updatedQuoteAfterOaf, ticket, agent, { resend: true });
      if (!ticketResult.ok) throw new Error(ticketResult.message);
      setQuotesById((prev) => {
        const next = new Map(prev);
        next.set(ticketResult.quote.id, ticketResult.quote);
        return next;
      });

      window.alert('All emails resent successfully (quote → OAF → e-ticket).');
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Could not resend emails';
      setError(msg);
    } finally {
      setResendAllInProgress(false);
    }
  }

  async function onAddNote(ticket: Ticket) {
    if (!agent) return;
    const message = noteText.trim();
    if (!message) {
      setError('Enter a note first.');
      return;
    }
    try {
      const updated = await addTicketNote(ticket.id, {
        message,
        author: agent.name || `agent-${agentId ?? 'unknown'}`,
      });
      setTickets((prev) => prev.map((x) => (x.id === updated.id ? updated : x)));
      setNoteText('');
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to add note');
    }
  }

  return (
    <div className="ticket-desk animate-fade">
      <header className="ticket-desk__header">
        <div>
          <h2>Ticket Desk</h2>
          <p>
            Select a ticket to see flight, passengers, and payment status. Quote follow-ups appear after you send a
            quote from Flight Search.
          </p>
        </div>
        <div className="ticket-desk__toolbar">
          <Search size={18} style={{ color: 'var(--text-muted)' }} aria-hidden />
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') void load(query);
            }}
            placeholder="Search code, client, title…"
          />
          <button type="button" className="ticket-desk__btn" onClick={() => void load(query)}>
            Search
          </button>
          <button
            type="button"
            className="ticket-desk__btn"
            onClick={() => {
              setQuery('');
              void load('');
            }}
          >
            Reset
          </button>
          <button
            type="button"
            className="ticket-desk__btn"
            onClick={() => {
              void load(query);
            }}
            title="Reload tickets and quotes"
          >
            <RefreshCw size={16} aria-hidden style={{ verticalAlign: 'middle', marginRight: 4 }} />
            Refresh
          </button>
        </div>
      </header>

      {error ? <div className="ticket-desk__alert">{error}</div> : null}

      <div className="ticket-desk__create">
        <button type="button" className="ticket-desk__create-toggle" onClick={() => setCreateOpen((o) => !o)}>
          <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <Plus size={18} aria-hidden />
            New manual ticket
          </span>
          {createOpen ? <ChevronUp size={18} aria-hidden /> : <ChevronDown size={18} aria-hidden />}
        </button>
        {createOpen ? (
          <form className="ticket-desk__create-form" onSubmit={onCreate}>
            <input
              value={form.title}
              onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))}
              placeholder="Title"
              required
            />
            <input
              value={form.clientName}
              onChange={(e) => setForm((p) => ({ ...p, clientName: e.target.value }))}
              placeholder="Client name"
              required
            />
            <input
              value={form.clientEmail}
              onChange={(e) => setForm((p) => ({ ...p, clientEmail: e.target.value }))}
              placeholder="Client email"
              required
            />
            <select value={form.priority} onChange={(e) => setForm((p) => ({ ...p, priority: e.target.value as TicketPriority }))}>
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
              <option value="urgent">Urgent</option>
            </select>
            <input
              value={form.tags}
              onChange={(e) => setForm((p) => ({ ...p, tags: e.target.value }))}
              placeholder="Tags (comma separated)"
            />
            <textarea
              value={form.description}
              onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
              placeholder="Description"
              rows={3}
              required
            />
            <button type="submit" className="ticket-desk__btn ticket-desk__btn--accent">
              Create ticket
            </button>
          </form>
        ) : null}
      </div>

      <div className="ticket-desk__layout">
        <aside className="ticket-desk__list-panel">
          <div className="ticket-desk__list-head">
            {loading ? 'Loading…' : `${tickets.length} ticket${tickets.length === 1 ? '' : 's'}`}
          </div>
          <div className="ticket-desk__list">
            {loading ? (
              <p style={{ padding: '1rem', color: 'var(--text-muted)', fontSize: '0.88rem' }}>Loading tickets…</p>
            ) : tickets.length === 0 ? (
              <p style={{ padding: '1rem', color: 'var(--text-muted)', fontSize: '0.88rem' }}>No tickets yet.</p>
            ) : (
              tickets.map((ticket) => {
                const quoteId = parseQuoteIdFromTicket(ticket);
                const quote = quoteId ? quotesById.get(quoteId) : undefined;
                const pay = paymentHint(ticket, quote);
                const quoteLinked = isQuoteFollowUpTicket(ticket);
                return (
                  <button
                    key={ticket.id}
                    type="button"
                    id={`ticket-row-${ticket.id}`}
                    className={`ticket-desk__list-item${selectedId === ticket.id ? ' is-selected' : ''}`}
                    onClick={() => setSelectedId(ticket.id)}
                  >
                    <div className="ticket-desk__list-item__top">
                      <span className="ticket-desk__list-item__code">{ticket.code}</span>
                      <span className={`ticket-desk__badge ${statusBadgeClass(ticket.status)}`}>
                        {formatStatusLabel(ticket.status)}
                      </span>
                    </div>
                    <p className="ticket-desk__list-item__title">{ticket.title}</p>
                    <p className="ticket-desk__list-item__meta">
                      {ticket.clientName}
                      {quoteLinked ? (
                        <>
                          {' '}
                          <span className="ticket-desk__badge ticket-desk__badge--quote">Quote</span>
                        </>
                      ) : null}
                    </p>
                    {pay ? (
                      <p className="ticket-desk__list-item__meta" style={{ marginTop: '0.2rem' }}>
                        <span className={`ticket-desk__badge ${pay === 'Paid' ? 'ticket-desk__badge--paid' : 'ticket-desk__badge--unpaid'}`}>
                          {pay}
                        </span>
                      </p>
                    ) : null}
                  </button>
                );
              })
            )}
          </div>
        </aside>

        <main className="ticket-desk__detail-panel">
          {!selectedTicket ? (
            <div className="ticket-desk__detail-empty">
              <p>Select a ticket from the list to view passengers, flight, and actions.</p>
            </div>
          ) : (
            <div className="ticket-desk__detail-inner">
              <header className="ticket-desk__detail-head">
                <div>
                  <h3>
                    {selectedTicket.code}
                    {isQuoteFollowUpTicket(selectedTicket) ? (
                      <span className="ticket-desk__badge ticket-desk__badge--quote" style={{ marginLeft: '0.5rem', verticalAlign: 'middle' }}>
                        Quote
                      </span>
                    ) : null}
                  </h3>
                  <p className="ticket-desk__detail-sub">{selectedTicket.title}</p>
                  <p className="ticket-desk__detail-sub">
                    {selectedTicket.clientName} · {selectedTicket.clientEmail}
                  </p>
                  <p className="ticket-desk__detail-sub" style={{ marginTop: '0.35rem' }}>
                    <span className={`ticket-desk__badge ${priorityBadgeClass(selectedTicket.priority)}`}>
                      {selectedTicket.priority}
                    </span>{' '}
                    <span className={`ticket-desk__badge ${statusBadgeClass(selectedTicket.status)}`}>
                      {formatStatusLabel(selectedTicket.status)}
                    </span>
                    {selectedTicket.tags.length > 0 ? (
                      <span style={{ marginLeft: '0.5rem', color: 'var(--text-muted)' }}>
                        · {selectedTicket.tags.join(', ')}
                      </span>
                    ) : null}
                  </p>
                </div>
                <div className="ticket-desk__detail-actions">
                  <button type="button" className="ticket-desk__btn" onClick={() => void onCycleStatus(selectedTicket)}>
                    Cycle status
                  </button>
                  {!isQuoteFollowUpTicket(selectedTicket) ? (
                    <button
                      type="button"
                      className="ticket-desk__btn ticket-desk__btn--accent"
                      disabled={emailingId === selectedTicket.id}
                      onClick={() => void onEmailClient(selectedTicket)}
                    >
                      <Mail size={16} aria-hidden style={{ verticalAlign: 'middle', marginRight: 4 }} />
                      {emailingId === selectedTicket.id ? 'Sending…' : 'Email client'}
                    </button>
                  ) : null}
                </div>
              </header>

              <TicketQuotePanel
                ticket={selectedTicket}
                quote={selectedQuote}
                agentId={agentId ?? ''}
                sendingTicketId={sendingTicketId}
                sendingOafId={sendingOafId}
                onSendTicketReceipt={() => void onSendTicketReceipt(selectedTicket)}
                onSendOafEmail={() => void onSendOafEmail(selectedTicket)}
                onCopy={copyText}
                onConfirmPayment={() => void onConfirmPayment(selectedTicket)}
                onConfirmAndSendTicketReceipt={() => void onConfirmAndSendTicketReceipt(selectedTicket)}
                onResendAllEmails={() => void onResendAllEmails(selectedTicket)}
                resendAllInProgress={resendAllInProgress}
              />

              <section className="ticket-desk__section">
                <h4 className="ticket-desk__section-title">Ticket notes</h4>
                <p className="ticket-desk__description">{selectedTicket.description}</p>
              </section>

              <section className="ticket-desk__section ticket-desk__notes">
                <h4 className="ticket-desk__section-title">Internal notes ({selectedTicket.notes.length})</h4>
                {selectedTicket.notes.length === 0 ? (
                  <p style={{ margin: 0, color: 'var(--text-muted)', fontSize: '0.88rem' }}>No notes yet.</p>
                ) : (
                  selectedTicket.notes.map((note) => (
                    <div key={note.id} className="ticket-desk__note">
                      <p>{note.message}</p>
                      <time>
                        {note.author} · {new Date(note.createdAt).toLocaleString()}
                      </time>
                    </div>
                  ))
                )}
                <textarea
                  rows={3}
                  value={noteText}
                  onChange={(e) => setNoteText(e.target.value)}
                  placeholder="Add an internal note…"
                  style={{ width: '100%', marginTop: '0.35rem' }}
                />
                <button
                  type="button"
                  className="ticket-desk__btn ticket-desk__btn--accent"
                  style={{ alignSelf: 'flex-start' }}
                  onClick={() => void onAddNote(selectedTicket)}
                >
                  Add note
                </button>
              </section>
            </div>
          )}
        </main>
      </div>
    </div>
  );
};

export default AdminTickets;
